// Size and colour picker shared by the featured product, shop-the-pair and the product page (assets/product.js).
// DE.Picker keeps the chosen colour and size for one product, from its variant data (snippets/variant-data.liquid),
// and listens to the colour radios of snippets/swatches.liquid and the size radios of snippets/size-options.liquid
// inside `root`. A colour change clears the size and redraws the sizes for that colour (the same markup as the
// snippet). Sold-out sizes drawn as aria-disabled radios (size-options `sold_radios`, the product page) can be
// focused but never chosen: a click on one is cancelled, and one checked by the arrow keys is unchecked again.
// The page's drawn choice is the truth: radios carry autocomplete="off" (no restored choices on a reload from
// history), and on pageshow the checked radios are put back in step with the choice in case a browser restored them.
// Also DE.showPrice(): a price display for the chosen variant (price, struck-through compare-at price, sale tag),
// DE.inlineAdded(): the inline "Added. <free-shipping message>" confirmation, DE.setBuyNow(): the state of the
// product page's Buy it now ('choose', 'ready' or 'hidden'), and DE.promptSize()/DE.clearSizePrompt(): the "Please
// select a size" prompt of every add button that stays active before a size is chosen (product page, featured
// product, shop-the-pair, phone quick-add sheet).
const DE = (window.DE = window.DE || {});
const strings = () => DE.config?.strings || {};
const esc = (value) => DE.escape(value);

class Picker {
  // options.onChange(picker, 'colour' | 'size') runs after each change.
  constructor(root, data, { onChange } = {}) {
    this.root = root;
    this.data = data;
    this.onChange = onChange;
    this.colour = data.colourIndex !== null ? data.colour ?? data.colours?.[0]?.name ?? null : null;
    // A size drawn as chosen (the product page's ?variant=).
    this.size = root.querySelector('[data-size-input][checked]')?.value ?? null;

    // A browser may restore old radio choices while the page loads (after this runs), without a change event.
    window.addEventListener('pageshow', () => this.syncInputs(), { once: true });
    root.addEventListener('change', (event) => this.handle(event));
    // A sold-out size (aria-disabled radio) is never chosen by a click or tap.
    root.addEventListener('click', (event) => {
      if (event.target.matches?.('[data-size-input][aria-disabled="true"]')) event.preventDefault();
    });
  }

  handle(event) {
    const input = event.target;
    if (input.matches('[data-size-input][aria-disabled="true"]')) {
      // The arrow keys checked a sold-out size: the choice goes back to what it was; focus stays for the tooltip.
      input.checked = false;
      const previous = [...this.root.querySelectorAll('[data-size-input]')].find((i) => i.value === this.size);
      if (previous) previous.checked = true;
      return;
    }
    if (input.matches('[data-colour-input]')) {
      this.colour = input.value;
      this.size = null;
      const name = this.root.querySelector('[data-colour-name]');
      if (name) name.textContent = input.value;
      this.renderSizes();
      this.onChange?.(this, 'colour');
    } else if (input.matches('[data-size-input]')) {
      this.size = input.value;
      this.onChange?.(this, 'size');
    }
  }

  // Checks the radios of the current choice (and only those).
  syncInputs() {
    this.root.querySelectorAll('[data-colour-input]').forEach((input) => (input.checked = input.value === this.colour));
    this.root.querySelectorAll('[data-size-input]').forEach((input) => (input.checked = input.value === this.size));
  }

  rowFor(size) {
    const { data } = this;
    return data.v.find(
      (r) => (data.sizeIndex === null || r[1] === size) && (data.colourIndex === null || r[2] === this.colour)
    );
  }

  // The chosen variant's row [id, size, colour, available, low, price, compare_at] once it can be added, else null.
  get variant() {
    if (this.data.sizeIndex !== null && this.size === null) return null;
    const row = this.rowFor(this.size);
    return row && row[3] ? row : null;
  }

  get ready() {
    return !!this.variant;
  }

  // The row whose price shows: the chosen size's (a ?variant= link can choose a sold-out one), else the chosen
  // colour's first variant with stock, else its first variant. The same variant as the server's price (main-product).
  get priceRow() {
    const { data } = this;
    if (data.sizeIndex === null || this.size !== null) {
      const row = this.rowFor(this.size);
      if (row) return row;
    }
    const inColour = (r) => data.colourIndex === null || r[2] === this.colour;
    return data.v.find((r) => r[3] && inColour(r)) || data.v.find(inColour) || null;
  }

  get price() {
    const row = this.priceRow;
    return row && row[5] !== undefined ? row[5] : this.data.price;
  }

  // The compare-at price that goes with `price` (the same variant's), or null.
  get compareAt() {
    const row = this.priceRow;
    return (row && row[6] !== undefined ? row[6] : this.data.compare_at) ?? null;
  }

  // "Only N left": the chosen size's stock once a size is chosen, else the colour's (or product's) total. 0 when
  // the count is above the threshold or not tracked.
  get low() {
    const threshold = Number(DE.config?.low_stock_threshold) || 0;
    let count = 0;
    if (this.size !== null) {
      const row = this.rowFor(this.size);
      count = row && row[3] && row[4] !== null ? row[4] : 0;
    } else {
      const rows = this.data.v.filter((r) => r[3] && (this.data.colourIndex === null || r[2] === this.colour));
      if (rows.length && !rows.some((r) => r[4] === null)) count = rows.reduce((sum, r) => sum + r[4], 0);
    }
    return count > 0 && count <= threshold ? count : 0;
  }

  get lowText() {
    const count = this.low;
    return count ? (strings().only_left || 'Only [count] left').replace('[count]', count) : '';
  }

  // soldRadios: sold-out sizes as aria-disabled radios with a tooltip (size-options `sold_radios`).
  sizesHTML(name, { soldRadios = false } = {}) {
    const sold = strings().sold_out || 'Sold out';
    const sizes = this.data.sizes?.length ? this.data.sizes : DE.sortSizes([...new Set(this.data.v.map((r) => r[1]))]);
    return sizes
      .map((value) => {
        const row = this.rowFor(value);
        if ((!row || !row[3]) && soldRadios) {
          return `<label class="size-opt size-opt--sold">
          <input class="size-opt__input" type="radio" name="${esc(name)}" value="${esc(value)}" aria-disabled="true"${value === this.size ? ' checked' : ''} autocomplete="off" data-size-input>
          <span>${esc(value)}</span><span class="visually-hidden">, ${esc(sold.toLowerCase())}</span><span class="size-opt__tip" aria-hidden="true">${esc(sold)}</span>
        </label>`;
        }
        if (!row || !row[3]) {
          return `<span class="size-opt size-opt--sold" title="${esc(sold)}">${esc(value)}<span class="visually-hidden">, ${esc(sold.toLowerCase())}</span></span>`;
        }
        return `<label class="size-opt">
          <input class="size-opt__input" type="radio" name="${esc(name)}" value="${esc(value)}"${value === this.size ? ' checked' : ''} autocomplete="off" data-size-input>
          <span>${esc(value)}</span>
        </label>`;
      })
      .join('');
  }

  renderSizes() {
    const list = this.root.querySelector('[data-size-list]');
    if (list) list.innerHTML = this.sizesHTML(list.dataset.name, { soldRadios: list.dataset.sold === 'radio' });
  }

  // True when the product has stock but none in the chosen colour (the add button then reads "Sold out").
  get colourSoldOut() {
    const { data } = this;
    return !!data.available && data.colourIndex !== null && !data.v.some((r) => r[3] && r[2] === this.colour);
  }
}

DE.Picker = Picker;

/* ---------- Size prompt ---------- */
// An add button pressed before a size is chosen sends nothing and calls DE.promptSize({ group, block, live, message,
// focus, reveal }):
// - group: the size radio group (fieldset); it is described by `live` (aria-describedby) while the prompt shows;
// - block: the element that gets .is-prompting, whose CSS outlines the sizes with stock (default: group); the class
//   is taken off and put back, so the short pulse restarts on a repeat;
// - live: the always-present aria-live region under the sizes; it is emptied at once and gets `message` two frames
//   later, so a screen reader hears the same text again;
// - focus (default true): focus moves to the first size with stock, without choosing it;
// - reveal: brings the sizes into view once the message is in (its line can move things): true (default) scrolls
//   the page, or the <dialog> the sizes are in, only when part of them is hidden (below the pinned header, or outside
//   the sheet), smoothly only when DE.motionOK(); a function does it its own way (the product page); false: no scroll.
// Returns false when there is no size with stock to choose. DE.clearSizePrompt({ group, block, live }) ends it (a
// size chosen, or a choice that can't be bought). Called with no argument, DE.promptSize() asks the product page's
// section, as the sticky bar does ("product:choose-size", assets/product.js).
const promptTokens = new WeakMap();

const revealSizes = (block) => {
  const rect = block.getBoundingClientRect();
  const behavior = DE.motionOK() ? 'smooth' : 'auto';
  const sheet = block.closest('dialog');
  if (sheet) {
    const frame = sheet.getBoundingClientRect();
    if (rect.top < frame.top) sheet.scrollBy({ top: rect.top - frame.top - 16, behavior });
    else if (rect.bottom > frame.bottom) sheet.scrollBy({ top: rect.bottom - frame.bottom + 16, behavior });
    return;
  }
  const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
  if (rect.top >= header - 1 && rect.bottom <= window.innerHeight + 1) return;
  // html's scroll-padding-top keeps the sizes below the pinned header.
  block.scrollIntoView({ block: 'nearest', behavior });
};

DE.promptSize = (options) => {
  if (!options) {
    DE.emit('product:choose-size');
    return true;
  }
  const { group, block = group, live, message, focus = true, reveal = true } = options;
  const first = group?.querySelector('[data-size-input]:not([aria-disabled="true"])');
  if (!first) return false;
  block.classList.remove('is-prompting');
  void block.offsetWidth;
  block.classList.add('is-prompting');
  if (focus) first.focus({ preventScroll: true });
  const show = () => {
    if (typeof reveal === 'function') reveal(block);
    else if (reveal) revealSizes(block);
  };
  if (!live) {
    show();
    return true;
  }
  const token = (promptTokens.get(live) || 0) + 1;
  promptTokens.set(live, token);
  live.textContent = '';
  if (live.id) group.setAttribute('aria-describedby', live.id);
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (promptTokens.get(live) !== token) return;
      live.textContent = message || 'Please select a size';
      show();
    })
  );
  return true;
};

DE.clearSizePrompt = ({ group, block = group, live } = {}) => {
  if (live) {
    promptTokens.set(live, (promptTokens.get(live) || 0) + 1);
    if (live.textContent) live.textContent = '';
  }
  group?.removeAttribute('aria-describedby');
  block?.classList.remove('is-prompting');
};

// Shows a variant's price in the price display inside `scope`: [data-price] gets the price; the struck-through
// compare-at price [data-compare] (amount in [data-compare-price]) and the sale tag [data-sale-tag] ("Sale −X%", the
// number in [data-sale-off]) show only while DE.saleOff finds a discount, and are hidden otherwise.
DE.showPrice = (scope, price, compare) => {
  if (!scope) return;
  const off = DE.saleOff(price, compare);
  const set = (selector, text) => scope.querySelectorAll(selector).forEach((node) => (node.textContent = text));
  set('[data-price]', DE.formatMoney(price));
  set('[data-compare-price]', off ? DE.formatMoney(compare) : '');
  set('[data-sale-off]', off ? String(off) : '');
  scope.querySelectorAll('[data-compare], [data-sale-tag]').forEach((node) => (node.hidden = !off));
};

// holder: an empty role="status" element (always in the page, so the text is announced). Shows "Added." (or
// `prefix`) and the free-shipping progress for the cart total (the markup of snippets/free-shipping-bar.liquid), then
// empties again after `seconds`.
const timers = new WeakMap();
DE.inlineAdded = (holder, cart, { seconds = 5, prefix: label } = {}) => {
  if (!holder) return;
  clearTimeout(timers.get(holder));
  const prefix = label || strings().added_short || 'Added.';
  const bar = cart ? DE.freeShippingHTML(cart.total_price, 'inline-added__shipping') : '';
  holder.innerHTML = `<div class="inline-added">${bar || '<p class="free-shipping__text"></p>'}</div>`;
  const text = holder.querySelector('.free-shipping__text');
  if (text) text.textContent = `${prefix} ${text.textContent}`.trim();
  timers.set(
    holder,
    setTimeout(() => (holder.innerHTML = ''), Math.max(1, seconds) * 1000)
  );
};

// The product page's Buy it now (<div data-buy-now>, sections/main-product.liquid):
// - 'choose' (no size with stock chosen yet): Shopify's button ([data-buy-now-button]) is inert and aria-hidden and
//   takes no pointer events (CSS), but still looks active; the theme's catcher button ([data-buy-now-catch]) lies
//   over it and asks for a size (product.js).
// - 'ready' (a size with stock is chosen): the catcher is hidden and Shopify's button is live.
// - 'hidden' (the chosen variant or colour is sold out): the whole wrapper is hidden.
// Shopify's button is shut before the catcher shows, and the catcher hidden before the button opens. The theme never
// clicks, focuses or submits Shopify's button.
DE.setBuyNow = (wrapper, state) => {
  if (!wrapper || wrapper.dataset.buyNowState === state) return;
  const button = wrapper.querySelector('[data-buy-now-button]');
  const catcher = wrapper.querySelector('[data-buy-now-catch]');
  wrapper.dataset.buyNowState = state;
  if (state === 'ready') {
    if (catcher) catcher.hidden = true;
    if (button) {
      button.inert = false;
      button.removeAttribute('aria-hidden');
    }
  } else {
    if (button) {
      button.inert = true;
      button.setAttribute('aria-hidden', 'true');
    }
    if (catcher) catcher.hidden = state !== 'choose';
  }
  wrapper.hidden = state === 'hidden';
};
